/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import {
    createSlice,
    ActionReducerMapBuilder,
    createAction,
} from '@reduxjs/toolkit';
import { fetchDashboardData } from './dashboardThunks';
import { SectionState } from './types';

export type DashboardState<TExtra = {}> = {
    selectedInterval: string;
    sections: Record<string, SectionState>;
} & TExtra;

export const setSelectedInterval = createAction<string>('setSelectedInterval');

export function createDashboardSlice<
    TExtraState extends Record<string, any> = {},
>(params: {
    name: string;
    initialState: DashboardState<TExtraState>;
    extraReducers?: (
        builder: ActionReducerMapBuilder<DashboardState<TExtraState>>
    ) => void;
}) {
    const { name, initialState, extraReducers } = params;

    return createSlice({
        name,
        initialState,
        reducers: {
            reset: () => initialState,
            updateSectionParams: (
                state,
                action: {
                    payload: {
                        sectionId: string;
                        params: Record<string, any>;
                    };
                }
            ) => {
                const { sectionId, params } = action.payload;
                const section = state.sections[sectionId];
                if (!section) return;

                section.params = {
                    ...(section.params ?? {}),
                    ...params,
                };
            },
        },
        extraReducers: builder => {
            /* ---------------------------
               DEFAULT dashboard reducers
            ----------------------------*/
            builder
                .addCase(fetchDashboardData.pending, (state, action) => {
                    const { dashboard, sections, clientId } = action.meta.arg;

                    if (dashboard !== name) return;

                    sections.forEach(s => {
                        const sectionId = composeSectionId(s.id, clientId);
                        const section = state.sections[sectionId];
                        if (section) {
                            section.loading = true;
                            section.error = null;
                            section.data = undefined;
                        } else {
                            state.sections[sectionId] = {
                                ...s,
                                loading: true,
                                error: null,
                                data: undefined,
                            };
                        }
                    });
                })
                .addCase(fetchDashboardData.fulfilled, (state, action) => {
                    const { dashboard, clientId } = action.meta.arg;

                    if (dashboard !== name) return;

                    action.payload.forEach(s => {
                        const sectionId = composeSectionId(s.id, clientId);
                        const section = state.sections[sectionId];
                        if (section) {
                            section.loading = false;
                            section.error = s.error ?? null;
                            section.data = s.result;
                        } else {
                            state.sections[sectionId] = {
                                ...s,
                                loading: false,
                                error: s.error ?? null,
                                data: s.result,
                            };
                        }
                    });
                })
                .addCase(fetchDashboardData.rejected, (state, action) => {
                    const { dashboard, sections, clientId } = action.meta.arg;

                    if (dashboard !== name) return;

                    sections.forEach(s => {
                        const sectionId = composeSectionId(s.id, clientId);
                        const section = state.sections[sectionId];
                        const error = 'Failed to fetch data';
                        if (section) {
                            section.loading = false;
                            section.error = error;
                        } else {
                            state.sections[sectionId] = {
                                ...s,
                                loading: false,
                                error,
                                data: null,
                            };
                        }
                    });
                })
                .addCase(setSelectedInterval, (state, action) => {
                    return {
                        ...state,
                        selectedInterval: action.payload,
                    };
                });

            /* ---------------------------
               EXTEND (optional)
            ----------------------------*/
            extraReducers?.(builder);
        },
    });
}

export function composeSectionId(sectionId: string, suffix?: string) {
    if (!suffix) {
        return sectionId;
    }
    return `${sectionId}-${suffix}`;
}
